import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { adminApi } from "@/api/admin";
import { Dialog } from "@/components/ui/Dialog";
import { Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/ip-blocks")({
  component: IpBlocksPage,
});

function IpBlocksPage() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["admin-ip-blocks"],
    queryFn: () => adminApi.listBlockedIps(),
    refetchInterval: 30_000,
  });
  const unblock = useMutation({
    mutationFn: (ip: string) => adminApi.unblockIp(ip),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-ip-blocks"] }),
  });
  const [open, setOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-2xl font-semibold">IP denylist</h1>
          <p className="mt-1 text-ink/65">Blocked at the backend-api request edge — checked before API-key resolution.</p>
        </div>
        <button onClick={() => setOpen(true)} className="btn-primary">
          <Plus size={16} className="mr-1" /> Block IP
        </button>
      </div>

      <div className="card overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase tracking-wide text-ink/55">
            <tr>
              <th className="px-4 py-3">IP</th>
              <th className="px-4 py-3">Reason</th>
              <th className="px-4 py-3">Blocked at</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? <tr><td colSpan={5} className="px-4 py-6 text-ink/55">Loading…</td></tr>
            : data?.blocks.length ? data.blocks.map((b) => (
              <tr key={b.ip} className="border-t border-ink/8">
                <td className="px-4 py-3 font-mono text-xs">{b.ip}</td>
                <td className="px-4 py-3">{b.reason ?? <span className="text-ink/45">—</span>}</td>
                <td className="px-4 py-3 text-ink/55">{b.blocked_at ? new Date(b.blocked_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-ink/55">
                  {b.ttl_secs < 0 ? "no expiry" : `${Math.round(b.ttl_secs / 60)} min`}
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    onClick={() => { if (confirm(`Unblock ${b.ip}?`)) unblock.mutate(b.ip); }}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 size={16} />
                  </button>
                </td>
              </tr>
            )) : <tr><td colSpan={5} className="px-4 py-6 text-ink/55">No blocked IPs.</td></tr>}
          </tbody>
        </table>
      </div>

      <BlockDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

function BlockDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (b: boolean) => void }) {
  const qc = useQueryClient();
  const [ip, setIp] = useState("");
  const [reason, setReason] = useState("");
  const [ttl, setTtl] = useState<"" | "1h" | "24h" | "7d" | "30d">("24h");

  const m = useMutation({
    mutationFn: () => {
      const ttlSecs = ttl === "" ? null
        : ttl === "1h"   ? 3600
        : ttl === "24h"  ? 86400
        : ttl === "7d"   ? 7 * 86400
        : 30 * 86400;
      return adminApi.blockIp(ip.trim(), { reason: reason.trim() || undefined, ttl_secs: ttlSecs });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-ip-blocks"] });
      onOpenChange(false);
      setIp(""); setReason(""); setTtl("24h");
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange} title="Block IP" description="Blocked traffic gets a 403 before reaching the API-key path.">
      <form onSubmit={(e) => { e.preventDefault(); if (ip.trim()) m.mutate(); }} className="space-y-3">
        <div>
          <label className="label">IP address</label>
          <input className="input mt-1 font-mono text-sm" required value={ip} onChange={(e) => setIp(e.target.value)} placeholder="1.2.3.4 or 2001:db8::1" />
        </div>
        <div>
          <label className="label">Reason (optional)</label>
          <input className="input mt-1" value={reason} onChange={(e) => setReason(e.target.value)} />
        </div>
        <div>
          <label className="label">Duration</label>
          <select className="input mt-1" value={ttl} onChange={(e) => setTtl(e.target.value as typeof ttl)}>
            <option value="1h">1 hour</option>
            <option value="24h">24 hours</option>
            <option value="7d">7 days</option>
            <option value="30d">30 days</option>
            <option value="">no expiry (manual unblock)</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={() => onOpenChange(false)} className="btn-secondary">Cancel</button>
          <button type="submit" className="btn-primary" disabled={m.isPending}>{m.isPending ? "Blocking…" : "Block"}</button>
        </div>
      </form>
    </Dialog>
  );
}
